/**
 * MetricCard — the one component every savings/evals surface renders through.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C05B. This component encodes
 * the campaign's binding cockpit rule (2026-07-23 integration amendment):
 * **missing telemetry renders as unavailable, never zero.**
 *
 * The four branches below are the complete evidence-state vocabulary of
 * C02D's `SavingsFigure.state` (`SAVINGS_DASHBOARD_SPEC.md` §8). Two of them
 * — `unavailable` and `withheld` — render no number at all:
 *
 * - `unavailable` means no receipt exists to compute the figure from.
 *   Rendering `0` or `0%` there would make the page claim "we measured and
 *   the answer was nothing", which is a different (and false) statement.
 * - `withheld` means something graded the run but the number is not
 *   projectable (e.g. a score on an unstated scale). Same rule: no number.
 *
 * The other two branches always print the denominator label beside the
 * value: a metric without a denominator is a number with no meaning, and
 * C02D's `SavingsFigure` carries `denominator_label` precisely so the UI
 * never has to invent one.
 *
 * Build status: compiled and exercised as part of the Vite customer SPA.
 */

import type { SavingsFigurePayload } from '../../api/evals';

interface MetricCardProps {
  title: string;
  /** `null` while the fetch has not resolved; the card says Loading, not 0. */
  figure: SavingsFigurePayload | null;
  /** True when the endpoint failed or is not mounted. */
  unavailable?: boolean;
}

function formatValue(figure: SavingsFigurePayload): string {
  // Only ever reached in the measured/estimated branches, where the server
  // guarantees `value` is present. The null-guard is defense in depth, not
  // a coalescing path: it renders "—" rather than a number.
  if (figure.value === null) {
    return '—';
  }
  if (figure.unit === 'fraction') {
    return `${(figure.value * 100).toFixed(1)}%`;
  }
  if (figure.unit === 'usd') {
    return `$${figure.value.toFixed(2)}`;
  }
  return `${figure.value} ${figure.unit}`;
}

export function MetricCard({ title, figure, unavailable }: MetricCardProps) {
  if (unavailable || figure === null) {
    // The endpoint is missing or the fetch has no result. Either way the
    // honest rendering is the unavailable branch — never a zero.
    return (
      <section className="metric-card" data-state="unavailable">
        <h3>{title}</h3>
        <p className="metric-value">Unavailable</p>
        <p className="metric-note">No telemetry to compute this figure from yet.</p>
      </section>
    );
  }

  if (figure.state === 'withheld') {
    return (
      <section className="metric-card" data-state="withheld">
        <h3>{title}</h3>
        <p className="metric-value">Withheld</p>
        <p className="metric-note">
          Receipts exist but the number is not projectable. Denominator:{' '}
          {figure.denominator_label}
        </p>
      </section>
    );
  }

  if (figure.state === 'unavailable') {
    return (
      <section className="metric-card" data-state="unavailable">
        <h3>{title}</h3>
        <p className="metric-value">Unavailable</p>
        <p className="metric-note">
          No receipt covers this figure. This is not a zero — the denominator
          ({figure.denominator_label}) has no data yet.
        </p>
      </section>
    );
  }

  // measured or estimated: a real number with its denominator stated.
  return (
    <section className="metric-card" data-state={figure.state}>
      <h3>{title}</h3>
      <p className="metric-value">{formatValue(figure)}</p>
      <p className="metric-note">
        {figure.state === 'estimated' ? 'Estimated — ' : 'Measured — '}
        denominator: {figure.denominator_label}
      </p>
      {figure.excluded_count > 0 ? (
        <p className="metric-note">
          {figure.excluded_count} run(s) excluded from this figure.
        </p>
      ) : null}
    </section>
  );
}
